import { useAppDispatch } from '@user/store/hooks'
import { useCallback } from 'react'
import {
  createSCWebHook,
  CustomHeaderValue,
  SalesCenterWebhook,
  SalesCenterWebhookModel,
  updateSCWebHook,
} from '@user/store/scWebHooks'
import { FormikErrors } from 'formik'
import { toast } from '@sales-center/components'
import { updateLoadingState } from '@sales-center/common'

export const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
export const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export const useActions = (handleClose: () => void) => {
  const dispatch = useAppDispatch()

  const handleSubmit = useCallback(
    async (model: SalesCenterWebhookModel) => {
      const isUpdate = Boolean(model.id)
      try {
        const webHookCreateModal: SalesCenterWebhook = {
          isEnabled: model.isEnabled,
          webhookType: model.webhookType,
          entityType:
            model.entityType.lead && model.entityType.opportunity
              ? 'Any'
              : model.entityType.lead
              ? 'Lead'
              : 'Opportunity',
          endpointUrl: model.endpointUrl,
          customHeaderValues: model.customHeaderValues,
          email: model.email,
        }

        if (isUpdate) {
          webHookCreateModal.id = model.id
        }

        dispatch(updateLoadingState('visible'))
        const webHookAction = isUpdate ? updateSCWebHook : createSCWebHook
        const result = await dispatch(webHookAction(webHookCreateModal))

        if (webHookAction.fulfilled.match(result)) {
          toast.success(
            `Webhook ${isUpdate ? 'updated' : 'created'} successfully.`,
          )
          handleClose()
        } else {
          toast.error(
            `Error when ${isUpdate ? 'updating' : 'creating'} webhook`,
          )
        }
      } catch (error) {
        toast.error(`Error when ${isUpdate ? 'updating' : 'creating'} webhook`)
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch, handleClose],
  )

  const validate = useCallback((model: SalesCenterWebhookModel) => {
    const errors: FormikErrors<SalesCenterWebhookModel> = {}

    if (!model.endpointUrl) {
      errors.endpointUrl = 'Required'
    } else if (!urlPattern.test(model.endpointUrl)) {
      errors.endpointUrl = 'Invalid URL'
    }

    if (!model.entityType.lead && !model.entityType.opportunity) {
      errors.entityType = {
        lead: 'Must select at least one record type',
        opportunity: 'Must select at least one record type',
      }
    }

    if (model.email && !emailPattern.test(model.email)) {
      errors.email = 'Must be a valid email address'
    }

    const customHeaderValuesErrors: CustomHeaderValue[] = []

    model.customHeaderValues?.forEach((header, index) => {
      customHeaderValuesErrors.push({
        key: '',
        value: '',
        error: '',
      })

      if ((header.key && !header.value) || (!header.key && header.value)) {
        customHeaderValuesErrors[index] = {
          ...customHeaderValuesErrors[index],
          error:
            'Custom header must have both key and value filled or both empty',
        }
      }
    })

    if (customHeaderValuesErrors.some((item) => item.error)) {
      errors.customHeaderValues = customHeaderValuesErrors
    }

    return errors
  }, [])

  return {
    handleSubmit,
    validate,
  }
}
